import { merge, range } from "lodash-es";

import {
  IBandSubscriptionParams,
  IEditBandSubscriptionParams,
} from "Bands/types";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { APIService } from "shared/services";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";

interface IJSONAPISubscription {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPISubscriptionCollection {
  [IDataKeys.subscription]: IJSONAPISubscription;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}
export interface IJSONAPISubscriptionRequestCollection {
  [IDataKeys.subscriptionRequest]: IJSONAPISubscription;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class SubscriptionService {
  static createSubscription = (
    data: IBandSubscriptionParams
  ): Promise<IJSONAPISubscriptionRequestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.SUBSCRIPTIONS,
          data,
        });
        resolve(response as IJSONAPISubscriptionRequestCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.POST_SUBSCRIPTION
        );
        reject(error);
      }
    });

  static editSubscription = (
    data: IEditBandSubscriptionParams
  ): Promise<IJSONAPISubscriptionRequestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const { subscriptionId, ...params } = data;
        const response = await APIService.post({
          path: API_ENDPOINTS.EDIT_SUBSCRIPTION(subscriptionId),
          data: params,
        });
        resolve(response as IJSONAPISubscriptionRequestCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.PATCH_SUBSCRIPTION
        );
        reject(error);
      }
    });

  static getSubscriptions = (
    queryParams?: {
      [key: string]: any;
    },
    isRecursive?: boolean
  ): Promise<IJSONAPISubscriptionCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        let subscriptions = await APIService.get({
          path: API_ENDPOINTS.SUBSCRIPTIONS,
          data: queryParams,
        });

        if (!isRecursive) {
          resolve(subscriptions as IJSONAPISubscriptionCollection);
          return;
        }

        // get all subscriptions until last page
        const { currentPage, totalPages } = subscriptions.pagination || {};
        const toFetchPages = range(currentPage + 1, totalPages + 1);

        for (const page of toFetchPages) {
          const newSubscription = await APIService.get({
            path: API_ENDPOINTS.SUBSCRIPTIONS,
            data: {
              ...queryParams,
              page,
            },
          });

          subscriptions = merge({}, subscriptions, newSubscription);
        }

        resolve(subscriptions as IJSONAPISubscriptionCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.GET_SUBSCRIPTIONS
        );
        reject(error);
      }
    });

  static startTrial = (
    bandId: string,
    start: string,
    stop: string
  ): Promise<IJSONAPISubscriptionCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const subscriptions = await APIService.post({
          path: API_ENDPOINTS.START_TRIAL,
          data: {
            band: bandId,
            start,
            stop,
          },
        });
        resolve(subscriptions as IJSONAPISubscriptionCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EDIT_TRIAL);
        reject(error);
      }
    });

  static editTrial = (
    subscriptionId: string,
    queryParams?: {
      [key: string]: any;
    }
  ): Promise<IJSONAPISubscriptionCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const subscriptions = await APIService.patch({
          path: API_ENDPOINTS.EDIT_TRIAL(subscriptionId),
          data: queryParams,
        });
        resolve(subscriptions as IJSONAPISubscriptionCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EDIT_TRIAL);
        reject(error);
      }
    });

  static changePaymentMethod = (
    subscriptionId: string,
    queryParams?: {
      [key: string]: any;
    }
  ): Promise<IJSONAPISubscriptionRequestCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.CHANGE_PAYMENT_METHOD(subscriptionId),
          data: queryParams,
        });
        resolve(response as IJSONAPISubscriptionRequestCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.POST_CHANGE_PAYMENT_METHOD
        );
        reject(error);
      }
    });

  static cancelSubscription = (subscriptionId: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.CANCEL_SUBSCRIPTION(subscriptionId),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.POST_CANCEL_SUBSCRIPTION
        );
        reject(error);
      }
    });

  static getPaymentInfo = ({
    id,
    redirectUrl,
  }: {
    id: string;
    redirectUrl: string;
  }) =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.COMPANY_PAYMENT_METHODS(id),
          data: {
            redirectUrl,
          },
        });

        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(
          API_ERROR_CODES.POST_GET_PAYMENT_INFO
        );
        reject(error);
      }
    });
}
